import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";

const IdleContext = createContext(null);

export function IdleProvider({
                                 idleAfterMs = 10_000,       // 10s for testing
                                 debug = false,
                                 events = ["pointerdown","pointerup","keydown","keyup","touchstart","touchend","click"],
                                 includeMouseMove = false,
                                 includeScroll = false,
                                 includeWheel = false,
                                 pollEveryMs = 1000,
                                 children,
                             }) {
    const [idle, setIdle] = useState(false);
    const lastActiveRef = useRef(Date.now());
    const pollRef = useRef(null);
    const lastMousemoveAtRef = useRef(0);

    const log = (...a) => { if (debug) console.log("[Idle]", ...a); };

    const markActivity = useCallback((type) => {
        if (type === "mousemove") {
            const now = Date.now();
            if (now - lastMousemoveAtRef.current < 250) return; // throttle noisy mousemove
            lastMousemoveAtRef.current = now;
        }
        lastActiveRef.current = Date.now();
        if (idle) {
            setIdle(false);
            // log("→ active (event:", type, ")");
        } else if (debug) {
            // log("activity:", type);
        }
    }, [idle, debug]);

    useEffect(() => {
        const opts = { passive: true };
        const onActivity = (e) => markActivity(e.type);
        const onVisibility = () => {
            if (document.visibilityState === "visible") markActivity("visibility");
        };
        const onFocus = () => markActivity("focus");

        const evts = [...events];
        if (includeMouseMove) evts.push("mousemove");
        if (includeScroll) evts.push("scroll");
        if (includeWheel) evts.push("wheel");

        evts.forEach((evt) => window.addEventListener(evt, onActivity, opts));
        document.addEventListener("visibilitychange", onVisibility, opts);
        window.addEventListener("focus", onFocus, opts);

        // Start polling (no "mount" activity)
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => {
            const elapsed = Date.now() - lastActiveRef.current;
            if (!idle && elapsed >= idleAfterMs) {
                setIdle(true);
                // log("→ idle (elapsed:", elapsed, "ms)");
            }
        }, pollEveryMs);

        return () => {
            evts.forEach((evt) => window.removeEventListener(evt, onActivity, opts));
            document.removeEventListener("visibilitychange", onVisibility, opts);
            window.removeEventListener("focus", onFocus, opts);
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [events, includeMouseMove, includeScroll, includeWheel, idle, idleAfterMs, pollEveryMs, markActivity, debug]);

    const value = useMemo(
        () => ({ idle, lastActiveAt: lastActiveRef.current }),
        [idle]
    );

    return <IdleContext.Provider value={value}>{children}</IdleContext.Provider>;
}

export function useIdle() {
    const ctx = useContext(IdleContext);
    if (!ctx) throw new Error("useIdle must be used within IdleProvider");
    return ctx;
}
